import {Component, OnInit} from '@angular/core';
import {NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {PayrollHttpService} from './payroll-http.service';
import {PayrollInvitation} from './payroll.models';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {InlineNoticeComponent} from '../../../_ui/components/feedback/inline-notice/inline-notice.component';
import {readErrorCode} from '../../../_auth/two-factor/second-factor-errors';
import {DEMO_COMPANY_INVITATION_HINT, describeDemoCompanyError} from '../../../_shared/errors/demo-company-errors';

/**
 * Company-owner management of external payroll accountants ("Lohnbuchhalter"): invite by email (with the
 * optional sensitive-data toggle), see who redeemed, and revoke access. Matches the app's general card style.
 */
@Component({
  selector: 'company-payroll-invitations',
  standalone: true,
  imports: [NgClass, FormsModule, InlineNoticeComponent],
  template: `
    <div class="mx-auto max-w-3xl space-y-6 p-4">
      <div>
        <h1 class="text-2xl font-bold tracking-tight text-gray-900">Lohnbuchhalter</h1>
        <p class="mt-1 text-sm text-gray-500">Laden Sie externe Lohnbuchhalter:innen ein und verwalten Sie deren Zugriff.</p>
      </div>

      <!-- Invite -->
      <div class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 class="text-sm font-semibold text-gray-900">Einladen</h2>
        <!-- #934: a demo company invites nobody. The form is not rendered at all rather than disabled, because
             there is no state in which it could become usable; the notice says why. The list below stays, so
             an invitation that somehow exists can still be withdrawn. -->
        @if (demoCompany) {
          <cqme-inline-notice class="mt-3" tone="info" id="payroll-demo-hint" data-test="payroll-demo-hint">
            {{ demoHint }}
          </cqme-inline-notice>
        } @else {
        <div class="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
          <label class="flex-1">
            <span class="mb-1 block text-xs font-medium text-gray-500">E-Mail</span>
            <input type="email" [(ngModel)]="email" placeholder="name@kanzlei.de"
                   class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-theme-primary-500 focus:outline-none focus:ring-1 focus:ring-theme-primary-500"/>
          </label>
          <label class="flex cursor-pointer items-center gap-2 pb-2 text-sm text-gray-600 select-none">
            <input type="checkbox" [(ngModel)]="canAccessSensitiveData"
                   class="h-4 w-4 rounded border-gray-300 text-theme-primary-600 focus:ring-theme-primary-500"/>
            Darf sensible Daten einsehen
          </label>
          <button type="button" (click)="invite()" [disabled]="busy || !email"
                  class="rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900 disabled:opacity-50">
            Einladen
          </button>
        </div>
        }

        @if (lastCode) {
          <div class="mt-3 rounded-lg bg-theme-primary-50 p-3 text-sm text-theme-primary-800">
            Einladung versendet. Code (einmalig):
            <span class="font-mono font-semibold">{{ lastCode }}</span>
          </div>
        }
      </div>

      <!-- List -->
      <div class="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <table class="min-w-full divide-y divide-gray-200 text-sm">
          <thead class="bg-gray-50">
            <tr class="text-left text-xs uppercase tracking-wide text-gray-500">
              <th class="px-4 py-3 font-semibold">E-Mail</th>
              <th class="px-4 py-3 font-semibold">Status</th>
              <th class="px-4 py-3 font-semibold">Sensible Daten</th>
              <!-- Named for screen readers: an empty header cell is an axe violation, and the column holds the row's action. -->
              <th class="px-4 py-3"><span class="sr-only">Aktionen</span></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            @for (i of invitations; track i.id) {
              <tr>
                <td class="px-4 py-3 font-medium text-gray-900">{{ i.email }}</td>
                <td class="px-4 py-3">
                  <span class="rounded-full px-2.5 py-0.5 text-xs font-semibold" [ngClass]="statusClass(i)">{{ statusLabel(i) }}</span>
                </td>
                <td class="px-4 py-3 text-gray-500">{{ i.canAccessSensitiveData ? 'Ja' : 'Nein' }}</td>
                <td class="px-4 py-3 text-right">
                  @if (!i.revoked) {
                    <button type="button" (click)="revoke(i)" [disabled]="busy"
                            class="text-xs font-semibold text-red-600 underline hover:text-red-800 disabled:opacity-50">widerrufen</button>
                  }
                </td>
              </tr>
            } @empty {
              <tr><td colspan="4" class="px-4 py-8 text-center text-sm italic text-gray-500">Noch keine Einladungen.</td></tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  `
})
export class PayrollInvitationsComponent implements OnInit {
  protected invitations: PayrollInvitation[] = [];
  protected email = '';
  protected canAccessSensitiveData = false;
  protected busy = false;
  protected lastCode: string | null = null;

  /** The sentence shown instead of the invite form in a demo company (#934). */
  protected readonly demoHint = DEMO_COMPANY_INVITATION_HINT;

  constructor(private http: PayrollHttpService, private gas: GlobalAlertService,
              private auth: AuthenticationService) {
  }

  /** Whether the session's company is a demo company, which refuses every invitation (#934). */
  protected get demoCompany(): boolean {
    return this.auth.isDemoCompany;
  }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.http.list().subscribe({
      next: list => this.invitations = list,
      error: () => this.gas.createAndShow('Fehler', 'Einladungen konnten nicht geladen werden.', AlertLevel.error)
    });
  }

  protected invite(): void {
    if (this.busy || !this.email || this.demoCompany) return;
    this.busy = true;
    this.http.invite({email: this.email, canAccessSensitiveData: this.canAccessSensitiveData}).subscribe({
      next: created => {
        this.lastCode = created.code ?? null;
        this.email = '';
        this.canAccessSensitiveData = false;
        this.busy = false;
        this.load();
      },
      error: err => {
        // #934: the server's demo refusal gets its own sentence; every other failure keeps the generic one.
        this.gas.createAndShow('Fehler', describeDemoCompanyError(readErrorCode(err)) ?? 'Einladung fehlgeschlagen.',
          AlertLevel.error);
        this.busy = false;
      }
    });
  }

  protected revoke(invitation: PayrollInvitation): void {
    if (this.busy) return;
    this.busy = true;
    this.http.revoke(invitation.id).subscribe({
      next: () => {
        this.busy = false;
        this.load();
      },
      error: () => {
        this.gas.createAndShow('Fehler', 'Widerruf fehlgeschlagen.', AlertLevel.error);
        this.busy = false;
      }
    });
  }

  protected statusLabel(i: PayrollInvitation): string {
    if (i.revoked) return 'Widerrufen';
    if (i.redeemed) return `Eingelöst${i.redeemedByName ? ' · ' + i.redeemedByName : ''}`;
    return 'Offen';
  }

  protected statusClass(i: PayrollInvitation): string {
    if (i.revoked) return 'bg-red-50 text-red-700';
    if (i.redeemed) return 'bg-green-50 text-green-700';
    return 'bg-amber-50 text-amber-700';
  }
}
